import { useEffect, useState } from "react";
import { User } from "snudbud-backbone-sdk";

interface ProgressBarProps {
  conversationTurns: number;
  sessionTargetMessages: number;
  profile?: User.FetchProfileReturn;
}

export default function ProgressBar({ conversationTurns, sessionTargetMessages, profile }: ProgressBarProps) {
  const [confetti, setConfetti] = useState<{ id: number; left: string; delay: string; color: string }[]>([]);
  const progressPercentage = Math.min((conversationTurns / sessionTargetMessages) * 100, 100);
  const goalReached = conversationTurns >= sessionTargetMessages;

  // Generate confetti effect when goal is reached
  useEffect(() => {
    if (conversationTurns === sessionTargetMessages) {
      // Create confetti pieces
      const confettiColors = ["#FF5A5F", "#3498DB", "#2ECC71", "#F1C40F", "#9B59B6"];
      const newConfetti = Array.from({ length: 20 }).map((_, index) => ({
        id: index,
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 1.5}s`,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
      }));
      setConfetti(newConfetti);
    }
  }, [conversationTurns, sessionTargetMessages]);

  return (
    <div className="px-4 py-2 bg-blue-50 border-b relative">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-blue-700">Conversation Turns</span>
        <span className="text-xs font-medium text-blue-700">
          {Math.min(conversationTurns, sessionTargetMessages)}/{sessionTargetMessages} exchanges
        </span>
      </div>
      <div className="w-full bg-blue-200 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ease-out ${
            goalReached ? "bg-green-500 animate-pulse" : "bg-blue-600"
          }`}
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>

      {/* Enhanced Goal Reached Notification */}
      {goalReached && (
        <div className="mt-2 bg-gradient-to-r from-blue-50 via-green-50 to-blue-50 rounded-lg p-3 border border-green-200 shadow-sm animate-slide-in-up relative overflow-hidden">
          {/* Confetti effect */}
          {confetti.map((piece) => (
            <div
              key={piece.id}
              className="confetti"
              style={{
                left: piece.left,
                animationDelay: piece.delay,
                backgroundColor: piece.color,
                top: "-5px",
              }}
            ></div>
          ))}

          <div className="animate-shine absolute inset-0 opacity-50 pointer-events-none"></div>
          <div className="flex items-center relative z-10">
            <div className="flex-shrink-0 bg-green-100 rounded-full p-1.5 mr-3 animate-celebrate">
              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Conversation Goal Reached!</p>
              <p className="text-xs text-gray-600">
                You and {profile?.name || "your buddy"} have exchanged {conversationTurns} conversation turns - keep
                going!
              </p>
            </div>
            <div className="ml-auto flex-shrink-0">
              <span className="inline-flex h-8 w-8 items-center justify-center animate-celebrate">
                <span className="text-xl">🎉</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
